import { analyzeManagedAiFixedRouteSource } from "@mph/managed-ai";

import { AI_PROVIDERS } from "./account-client";
import { projectRelativePath, type SelectedFile } from "./folder";

export type AIProvider = "anthropic" | "google" | "openai";
export type AIEndpoint = "messages" | "responses" | "chat-completions" | "generate-content";
export type AIKeyLocation = "omitted-env" | "publish-output";

export interface AIProviderReport {
  provider: AIProvider;
  endpoints: AIEndpoint[];
  models: string[];
  selectedModel: string | null;
  keyLocations: AIKeyLocation[];
}

export interface AIIntakeReport {
  status: "none" | "ready" | "blocked";
  fixedRoute?: boolean;
  reasonCode?: "AI_FIXED_ROUTE_REQUIRED" | "AI_LEGACY_ROUTE_FORBIDDEN" | "SECRET_DETECTED";
  providers: AIProviderReport[];
  sanitizedPaths: string[];
  blockedPaths: string[];
}

export interface AIIntakeResult {
  report: AIIntakeReport;
  files: SelectedFile[];
}

interface MutableProviderReport {
  endpoints: Set<AIEndpoint>;
  models: Set<string>;
  keyLocations: Set<AIKeyLocation>;
}

interface PublishText {
  selected: SelectedFile;
  text: string;
  keyProviders: Set<AIProvider>;
  directProviderCall: boolean;
  legacyRoute: boolean;
  incompleteFixedRoute: boolean;
}

const ENDPOINTS: ReadonlyArray<{ provider: AIProvider; endpoint: AIEndpoint; url: string }> = [
  { provider: "anthropic", endpoint: "messages", url: "https://api.anthropic.com/v1/messages" },
  { provider: "openai", endpoint: "responses", url: "https://api.openai.com/v1/responses" },
  { provider: "openai", endpoint: "chat-completions", url: "https://api.openai.com/v1/chat/completions" },
  { provider: "google", endpoint: "generate-content", url: "https://generativelanguage.googleapis.com/" },
];

const MAX_FILE_BYTES = 1024 * 1024;
const MAX_TOTAL_BYTES = 5 * 1024 * 1024;
const ANTHROPIC_KEY = /sk-ant-[A-Za-z0-9_-]{20,}/g;
const OPENAI_KEY = /sk-(?!ant-)[A-Za-z0-9_-]{20,}/gi;
const DISTINCTIVE_OPENAI_KEY = /\bsk-(?:proj|svcacct)-[A-Za-z0-9_-]{20,}/i;
const GOOGLE_KEY = /AIza[A-Za-z0-9_-]{30,}/g;
const MODEL_LITERAL = /(?:["']?model["']?\s*[:=]\s*["'])([^"'\s]{2,100})["']/g;
const LEGACY_ROUTE = /\/\.mph\/(?:anthropic\/v1\/messages|openai\/v1\/(?:responses|chat\/completions))/;
const INSPECTABLE_PATH = /(?:^|\/)(?:\.env(?:\.[^/]+)?|[^/]+\.(?:c?js|mjs|jsx|ts|tsx|json|html?|css|svg|xml|txt|md|map|vue|svelte|astro|py|rb|php|go|rs|java|kt|swift|sh|ya?ml|toml|ini))$/i;
const MAX_EXTENSIONLESS_TEXT_BYTES = 256 * 1024;

function mutableProvider(
  reports: Map<AIProvider, MutableProviderReport>,
  provider: AIProvider,
): MutableProviderReport {
  let report = reports.get(provider);
  if (!report) {
    report = { endpoints: new Set(), models: new Set(), keyLocations: new Set() };
    reports.set(provider, report);
  }
  return report;
}

function isEnvPath(path: string): boolean {
  return /(?:^|\/)\.env(?:\.[^/]+)?$/i.test(path) && !/\.env\.example$/i.test(path);
}

function isInspectableFile(path: string, file: File): boolean {
  if (INSPECTABLE_PATH.test(path) || file.type.startsWith("text/")) return true;
  const name = path.split("/").at(-1) ?? path;
  return !name.includes(".") && file.size <= MAX_EXTENSIONLESS_TEXT_BYTES;
}

function hasProviderSignal(text: string, provider: AIProvider): boolean {
  if (provider === "anthropic") {
    return /@anthropic-ai\/sdk|\bnew\s+Anthropic\b|\banthropic\.messages\.create\b/.test(text);
  }
  if (provider === "google") {
    return /@google\/(?:generative-ai|genai)|\bGoogleGenerativeAI\b|\bGoogleGenAI\b/.test(text);
  }
  return /(?:from\s*["']openai["']|require\(["']openai["']\)|\bnew\s+OpenAI\b|\bopenai\.(?:responses|chat\.completions)\.create\b)/.test(text);
}

function isModelForProvider(value: string, provider: AIProvider): boolean {
  if (provider === "anthropic") return /^claude-[A-Za-z0-9._:-]+$/.test(value);
  if (provider === "google") return /^gemini-[A-Za-z0-9._:-]+$/.test(value);
  return /^(?:(?:gpt|chatgpt|codex)-[A-Za-z0-9._:-]+|o[1-9](?:-[A-Za-z0-9._:-]+)?)$/.test(value);
}

function modelLiterals(text: string, provider: AIProvider): string[] {
  const models = new Set<string>();
  for (const match of text.matchAll(MODEL_LITERAL)) {
    const value = match[1];
    if (value && isModelForProvider(value, provider)) models.add(value);
  }
  return [...models];
}

function hasAnthropicKey(text: string): boolean {
  ANTHROPIC_KEY.lastIndex = 0;
  return ANTHROPIC_KEY.test(text);
}

function hasOpenAIKey(text: string, hasOpenAIContext: boolean): boolean {
  if (DISTINCTIVE_OPENAI_KEY.test(text)) return true;
  if (!hasOpenAIContext && !/\bOPENAI_API_KEY\b/i.test(text)) return false;
  OPENAI_KEY.lastIndex = 0;
  return OPENAI_KEY.test(text);
}

function hasGoogleKey(text: string): boolean {
  GOOGLE_KEY.lastIndex = 0;
  return GOOGLE_KEY.test(text);
}

function readFileText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(String(reader.result ?? "")));
    reader.addEventListener("error", () => reject(new Error(`Could not inspect ${file.name}.`)));
    reader.readAsText(file);
  });
}

function finalizedReports(reports: Map<AIProvider, MutableProviderReport>): AIProviderReport[] {
  const providerOrder: AIProvider[] = [...AI_PROVIDERS];
  return providerOrder.flatMap((provider) => {
    const report = reports.get(provider);
    if (!report) return [];
    const models = [...report.models].sort();
    return [{
      provider,
      endpoints: [...report.endpoints].sort(),
      models,
      selectedModel: models.length === 1 ? models[0]! : null,
      keyLocations: [...report.keyLocations].sort(),
    }];
  });
}

export async function inspectAIIntake(
  originalFiles: readonly File[],
  publishFiles: readonly SelectedFile[],
): Promise<AIIntakeResult> {
  const reports = new Map<AIProvider, MutableProviderReport>();
  let fixedRoute = false;

  for (const file of originalFiles) {
    const path = projectRelativePath(file);
    if (!isEnvPath(path)) continue;
    if (file.size > MAX_FILE_BYTES) throw new Error(`Could not safely inspect ${path}.`);
    const text = await readFileText(file);
    if (/^\s*ANTHROPIC_API_KEY\s*=\s*\S+/m.test(text)) {
      mutableProvider(reports, "anthropic").keyLocations.add("omitted-env");
    }
    if (/^\s*OPENAI_API_KEY\s*=\s*\S+/m.test(text)) {
      mutableProvider(reports, "openai").keyLocations.add("omitted-env");
    }
    if (/^\s*(?:GEMINI|GOOGLE)_API_KEY\s*=\s*\S+/m.test(text)) {
      mutableProvider(reports, "google").keyLocations.add("omitted-env");
    }
  }

  const publishTexts: PublishText[] = [];
  let totalBytes = 0;
  for (const selected of publishFiles) {
    if (!isInspectableFile(selected.path, selected.file)) continue;
    if (selected.file.size > MAX_FILE_BYTES) throw new Error(`Could not safely inspect ${selected.path}.`);
    totalBytes += selected.file.size;
    if (totalBytes > MAX_TOTAL_BYTES) throw new Error("Could not safely inspect this folder.");
    const text = await readFileText(selected.file);
    const routeUsage = analyzeManagedAiFixedRouteSource(text);
    const fixedRouteJsonPost = routeUsage.validPostCount > 0;
    fixedRoute ||= fixedRouteJsonPost;
    const providersInFile = new Set<AIProvider>();
    for (const candidate of ENDPOINTS) {
      if (!text.includes(candidate.url)) continue;
      providersInFile.add(candidate.provider);
      mutableProvider(reports, candidate.provider).endpoints.add(candidate.endpoint);
    }
    for (const provider of AI_PROVIDERS) {
      if (hasProviderSignal(text, provider)) providersInFile.add(provider);
    }
    for (const provider of providersInFile) {
      const report = mutableProvider(reports, provider);
      for (const model of modelLiterals(text, provider)) report.models.add(model);
    }

    const keyProviders = new Set<AIProvider>();
    if (hasAnthropicKey(text)) keyProviders.add("anthropic");
    if (hasOpenAIKey(text, providersInFile.has("openai"))) keyProviders.add("openai");
    if (hasGoogleKey(text)) keyProviders.add("google");
    for (const provider of keyProviders) {
      mutableProvider(reports, provider).keyLocations.add("publish-output");
    }
    publishTexts.push({
      selected,
      text,
      keyProviders,
      directProviderCall: providersInFile.size > 0,
      legacyRoute: LEGACY_ROUTE.test(text),
      incompleteFixedRoute: routeUsage.callCount > routeUsage.validPostCount,
    });
  }

  if (reports.has("openai")) {
    for (const entry of publishTexts) {
      if (!hasOpenAIKey(entry.text, true) || entry.keyProviders.has("openai")) continue;
      entry.keyProviders.add("openai");
      mutableProvider(reports, "openai").keyLocations.add("publish-output");
    }
  }

  const blockedPaths = publishTexts
    .filter(({ keyProviders, directProviderCall, legacyRoute, incompleteFixedRoute }) => keyProviders.size > 0 || directProviderCall || legacyRoute || incompleteFixedRoute)
    .map(({ selected }) => selected.path)
    .sort();
  if (blockedPaths.length > 0) {
    return {
      report: {
        status: "blocked",
        fixedRoute,
        reasonCode: publishTexts.some(({ keyProviders }) => keyProviders.size > 0)
          ? "SECRET_DETECTED"
          : publishTexts.some(({ legacyRoute }) => legacyRoute)
            ? "AI_LEGACY_ROUTE_FORBIDDEN"
            : "AI_FIXED_ROUTE_REQUIRED",
        providers: finalizedReports(reports),
        sanitizedPaths: [],
        blockedPaths,
      },
      files: [],
    };
  }

  const providers = finalizedReports(reports);
  return {
    report: {
      status: fixedRoute || providers.length > 0 ? "ready" : "none",
      fixedRoute,
      providers,
      sanitizedPaths: [],
      blockedPaths: [],
    },
    files: [...publishFiles],
  };
}
